import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { journalEntriesService } from '../../../services/accounting/journal-entries';
import { invoicesService } from '../../../services/accounting/invoices';
import { expensesService } from '../../../services/accounting/expenses';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import { shipmentsService } from '../../../services/logistics/shipments';
import { billsService } from '../../../services/payables/bills';
import { reportsService } from '../../../services/reports/reportsService';

/**
 * ReportsTab Component
 * 
 * Comprehensive financial reporting interface with multiple report types.
 * Includes overview, profit & loss, balance sheet, and other financial reports.
 */

const ReportsTab = ({ financialData }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [selectedReport, setSelectedReport] = useState('overview');
  const [dateRange, setDateRange] = useState('current_month');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [reportData, setReportData] = useState({
    invoices: [],
    expenses: [],
    journalEntries: [],
    chartOfAccounts: [],
    shipments: [],
    bills: []
  });
  const [filteredFinancialData, setFilteredFinancialData] = useState(financialData);

  const loadReportData = useCallback(async () => {
    if (!currentCompany?.id) return;

    try {
      setLoading(true);
      const [invoices, expenses, journalEntries, chartOfAccounts, shipments, bills] = await Promise.all([
        invoicesService.getInvoices(currentCompany.id),
        expensesService.getExpenses(currentCompany.id),
        journalEntriesService.getJournalEntries(currentCompany.id),
        chartOfAccountsService.getChartOfAccounts(currentCompany.id),
        shipmentsService.getShipments(currentCompany.id),
        billsService.getBills(currentCompany.id)
      ]);

      setReportData({
        invoices,
        expenses,
        journalEntries,
        chartOfAccounts,
        shipments,
        bills
      });
    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  const getDateRangeFilter = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'current_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'current_quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        startDate = new Date(today.getFullYear(), currentQuarter * 3, 1);
        endDate = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
        break;
      case 'last_quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const quarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        startDate = new Date(quarterYear, adjustedQuarter * 3, 1);
        endDate = new Date(quarterYear, (adjustedQuarter + 1) * 3, 0);
        break;
      case 'current_year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31);
        break;
      case 'last_year':
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'custom':
        startDate = customDateRange.start ? new Date(customDateRange.start) : new Date(today.getFullYear(), 0, 1);
        endDate = customDateRange.end ? new Date(customDateRange.end) : today;
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }

    return { startDate, endDate };
  };

  const filterDataByDateRange = useCallback(() => {
    const { startDate, endDate } = getDateRangeFilter();

    // Filter invoices
    const filteredInvoices = reportData.invoices.filter(invoice => {
      const invoiceDate = new Date(invoice.issueDate || invoice.createdAt?.toDate?.() || invoice.createdAt);
      return invoiceDate >= startDate && invoiceDate <= endDate;
    });

    // Filter expenses
    const filteredExpenses = reportData.expenses.filter(expense => {
      const expenseDate = new Date(expense.date || expense.createdAt?.toDate?.() || expense.createdAt);
      return expenseDate >= startDate && expenseDate <= endDate;
    });

    // Calculate filtered financial metrics
    const totalRevenue = filteredInvoices.reduce((sum, invoice) => sum + (invoice.finalTotal || invoice.total || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + (expense.totalAmount || expense.amount || 0), 0);
    const vatFromInvoices = filteredInvoices.reduce((sum, invoice) => sum + (invoice.vatAmount || 0), 0);
    const vatFromExpenses = filteredExpenses.reduce((sum, expense) => sum + (expense.vatAmount || 0), 0);

    setFilteredFinancialData({
      ...financialData,
      revenue: totalRevenue,
      expenses: totalExpenses,
      profit: totalRevenue - totalExpenses,
      vatPayable: vatFromInvoices - vatFromExpenses,
      filteredInvoices,
      filteredExpenses
    });
  }, [dateRange, customDateRange, reportData, financialData]);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  // Filter data when date range changes
  useEffect(() => {
    filterDataByDateRange();
  }, [filterDataByDateRange]);

  // Available reports
  const availableReports = [
    {
      id: 'overview',
      name: t('clients.overview'),
      icon: '📊',
      description: t('reports.overview_desc', 'High-level financial summary'),
      color: 'blue'
    },
    {
      id: 'profit_loss',
      name: t('reports.profit_loss', 'Profit & Loss'),
      icon: '📈',
      description: t('reports.profit_loss_desc', 'Income statement showing revenue and expenses'),
      color: 'green'
    },
    {
      id: 'balance_sheet',
      name: t('accounting.balance_sheet'),
      icon: '⚖️',
      description: t('reports.balance_sheet_desc', 'Assets, liabilities, and equity'),
      color: 'purple'
    },
    {
      id: 'cash_flow',
      name: t('accounting.cash_flow'),
      icon: '💰',
      description: t('reports.cash_flow_desc', 'Cash inflows and outflows'),
      color: 'yellow'
    },
    {
      id: 'tax_report',
      name: t('accounting.tax_report'),
      icon: '🧾',
      description: t('reports.tax_report_desc', 'VAT and tax calculations'),
      color: 'red'
    },
    {
      id: 'aging_report',
      name: t('accounting.aging_report'),
      icon: '📅',
      description: t('payment_tracking.aging_report_desc'),
      color: 'orange'
    },
    {
      id: 'trial_balance',
      name: t('accounting.trial_balance'),
      icon: '⚖️',
      description: t('reports.trial_balance_desc', 'Trial balance of all accounts'),
      color: 'indigo'
    },
    {
      id: 'general_ledger',
      name: t('accounting.general_ledger'),
      icon: '📚',
      description: t('reports.general_ledger_desc', 'Detailed account transactions'),
      color: 'gray'
    }
  ];

  // Date range options
  const dateRangeOptions = [
    { value: 'current_month', label: t('accounting.current_month') },
    { value: 'last_month', label: t('chart.last_month') },
    { value: 'current_quarter', label: t('reports.date.current_quarter', 'Current Quarter') },
    { value: 'last_quarter', label: t('chart.last_quarter') },
    { value: 'current_year', label: t('reports.date.current_year', 'Current Year') },
    { value: 'last_year', label: t('chart.last_year') },
    { value: 'custom', label: t('reports.date.custom', t('common.custom_range')) }
  ];

  const generateReport = async (reportType, format = 'pdf') => {
    setLoading(true);
    setLoadingMessage(format === 'pdf' ? t('reports.generating_pdf', 'Generating PDF...') : t('reports.generating_excel', 'Generating Excel...'));
    
    try {
      const { startDate, endDate } = getDateRangeFilter();
      const reportTitle = availableReports.find(r => r.id === reportType)?.name || reportType;

      if (format === 'excel') {
        setLoadingMessage(t('reports.preparing_excel', 'Preparing Excel file...'));
        await generateExcelReport(reportType, reportTitle, startDate, endDate);
      } else {
        setLoadingMessage(t('reports.generating_pdf_server', 'Generating PDF on server...'));
        await generatePDFReport(reportType, reportTitle, startDate, endDate);
      }

      toast.success(`${reportTitle} report generated successfully`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const generatePDFReport = async (reportType, reportTitle, startDate, endDate) => {
    try {
      // Prepare report data
      const reportData = prepareReportData(reportType);
      
      const dateRange = {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        label: getDateRangeDisplay()
      };

      // Generate PDF using the service
      const blob = await reportsService.generatePDF(
        reportType,
        reportData,
        currentCompany?.id,
        dateRange,
        'ar' // You can make this dynamic based on user preference
      );

      // Download the PDF
      const filename = `${reportTitle.replace(/\s+/g, '_')}_${startDate.toISOString().split('T')[0]}_to_${endDate.toISOString().split('T')[0]}.pdf`;
      reportsService.downloadBlob(blob, filename);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw error;
    }
  };

  const generateExcelReport = async (reportType, reportTitle, startDate, endDate) => {
    try {
      const XLSX = await import('xlsx');
      
      // Prepare report data
      const reportData = prepareReportData(reportType);
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add company info sheet
      const companyInfo = [
        [currentCompany?.companyName || currentCompany?.name || 'شركة النقل'],
        [''],
        [reportTitle],
        [`${t('reports.period', 'Period')}: ${getDateRangeDisplay()}`],
        [`${t('reports.generated_on', 'Generated on')}: ${new Date().toLocaleDateString('en-GB')}`],
        [`${t('reports.report_time', 'Report Time')}: ${new Date().toLocaleTimeString('en-GB', {hour12: false})}`],
        ['']
      ];
      
      // Add summary data
      if (reportData.summary) {
        companyInfo.push(
          [t('accounting.total_revenue'), `${reportData.summary.revenue?.toLocaleString() || 0} ${t('currency.sar', 'SAR')}`],
          [t('accounting.total_expenses'), `${reportData.summary.expenses?.toLocaleString() || 0} ${t('currency.sar', 'SAR')}`],
          [t('accounting.net_profit'), `${reportData.summary.profit?.toLocaleString() || 0} ${t('currency.sar', 'SAR')}`],
          ['']
        );
      }
      
      // Add detailed data if available
      if (reportData.tableData && reportData.tableData.length > 0) {
        // Add headers
        if (reportData.tableHeaders) {
          companyInfo.push(reportData.tableHeaders);
        }
        
        // Add data rows
        reportData.tableData.forEach(row => {
          companyInfo.push(Object.values(row));
        });
      }
      
      const ws = XLSX.utils.aoa_to_sheet(companyInfo);
      
      // Set column widths
      ws['!cols'] = [
        { width: 30 },
        { width: 20 },
        { width: 15 },
        { width: 15 }
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, reportTitle);
      
      // Generate and download Excel file
      const fileName = `${reportTitle.replace(/\s+/g, '_')}_${startDate.toISOString().split('T')[0]}_to_${endDate.toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Error generating Excel report:', error);
      throw error;
    }
  };

  const prepareReportData = (reportType) => {
    const data = {
      summary: {
        revenue: filteredFinancialData.revenue || 0,
        expenses: filteredFinancialData.expenses || 0,
        profit: filteredFinancialData.profit || 0
      },
      tableData: [],
      tableHeaders: []
    };

    switch (reportType) {
      case 'profit_loss':
        data.tableHeaders = [t('accounting.account'), t('accounting.amount')];
        data.tableData = [
          { account: t('accounting.revenue'), amount: `${(filteredFinancialData.revenue || 0).toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { account: t('accounting.expenses'), amount: `${(filteredFinancialData.expenses || 0).toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { account: t('accounting.net_profit'), amount: `${(filteredFinancialData.profit || 0).toLocaleString()} ${t('currency.sar', 'SAR')}` }
        ];
        break;
        
      case 'balance_sheet':
        const assetAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'asset');
        const liabilityAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'liability');
        const equityAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'equity');
        
        data.tableHeaders = [t('accounting.account_name'), t('accounting.account_type'), t('accounting.balance')];
        data.tableData = [
          ...assetAccounts.map(acc => ({
            account: acc.accountName,
            type: t('accounting.assets'),
            balance: `${(acc.currentBalance || 0).toLocaleString()} ${t('currency.sar', 'SAR')}`
          })),
          ...liabilityAccounts.map(acc => ({
            account: acc.accountName,
            type: t('accounting.liabilities'),
            balance: `${(acc.currentBalance || 0).toLocaleString()} ${t('currency.sar', 'SAR')}`
          })),
          ...equityAccounts.map(acc => ({
            account: acc.accountName,
            type: t('accounting.equity'),
            balance: `${(acc.currentBalance || 0).toLocaleString()} ${t('currency.sar', 'SAR')}`
          }))
        ];
        break;
        
      case 'aging_report':
        const today = new Date();
        const aging = { current: 0, days30: 0, days60: 0, days90: 0, over90: 0 };
        
        reportData.invoices.forEach(invoice => {
          if (invoice.paymentStatus !== 'paid') {
            const invoiceDate = new Date(invoice.issueDate || invoice.createdAt?.toDate?.() || invoice.createdAt);
            const daysDiff = Math.floor((today - invoiceDate) / (1000 * 60 * 60 * 24));
            const amount = invoice.remainingAmount || invoice.totalAmount || 0;

            if (daysDiff <= 30) aging.current += amount;
            else if (daysDiff <= 60) aging.days30 += amount;
            else if (daysDiff <= 90) aging.days60 += amount;
            else if (daysDiff <= 120) aging.days90 += amount;
            else aging.over90 += amount;
          }
        });
        
        data.tableHeaders = [t('accounting.age_period'), t('accounting.amount')];
        data.tableData = [
          { period: t('accounting.current_30_days'), amount: `${aging.current.toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { period: t('accounting.days_31_60'), amount: `${aging.days30.toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { period: t('accounting.days_61_90'), amount: `${aging.days60.toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { period: t('accounting.days_91_120'), amount: `${aging.days90.toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { period: t('accounting.over_120_days'), amount: `${aging.over90.toLocaleString()} ${t('currency.sar', 'SAR')}` }
        ];
        break;
        
      default:
        // Overview or other reports
        data.tableHeaders = [t('accounting.metric'), t('accounting.value')];
        data.tableData = [
          { metric: t('accounting.total_revenue'), value: `${(filteredFinancialData.revenue || 0).toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { metric: t('accounting.total_expenses'), value: `${(filteredFinancialData.expenses || 0).toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { metric: t('accounting.net_profit'), value: `${(filteredFinancialData.profit || 0).toLocaleString()} ${t('currency.sar', 'SAR')}` },
          { metric: t('chart.profit_margin'), value: `${filteredFinancialData.revenue > 0 ? ((filteredFinancialData.profit / filteredFinancialData.revenue) * 100).toFixed(1) : '0.0'}%` }
        ];
    }

    return data;
  };

  const generatePDFContent = (reportType, title, startDate, endDate) => {
    // Format date as DD/MM/YYYY
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const dateRangeText = `${formatDate(startDate)} to ${formatDate(endDate)}`;
    let content = `${title}\n${dateRangeText}\n${'='.repeat(50)}\n\n`;

    switch (reportType) {
      case 'profit_loss':
        content += generateProfitLossContent();
        break;
      case 'balance_sheet':
        content += generateBalanceSheetContent();
        break;
      case 'cash_flow':
        content += generateCashFlowContent();
        break;
      case 'tax_report':
        content += generateTaxReportContent();
        break;
      case 'aging_report':
        content += generateAgingReportContent();
        break;
      case 'trial_balance':
        content += generateTrialBalanceContent();
        break;
      default:
        content += generateOverviewContent();
    }

    return content;
  };

  const generateProfitLossContent = () => {
    let content = `${t('accounting.profit_loss_statement')}\n`;
    content += `${t('invoices.period')}: ${getDateRangeDisplay()}\n\n`;
    content += `${t('accounting.revenue')}: SAR ${filteredFinancialData.revenue.toLocaleString()}\n`;
    content += `${t('accounting.expenses')}: SAR ${filteredFinancialData.expenses.toLocaleString()}\n`;
    content += `${t('accounting.net_profit')}: SAR ${filteredFinancialData.profit.toLocaleString()}\n\n`;

    // Add real expense breakdown from actual expense data
    const expenseBreakdown = {};
    filteredFinancialData.filteredExpenses?.forEach(expense => {
      const category = expense.category || 'Other Expenses';
      if (!expenseBreakdown[category]) {
        expenseBreakdown[category] = 0;
      }
      expenseBreakdown[category] += expense.totalAmount || expense.amount || 0;
    });

    // Helper function to get translated category name for PDF
    const getCategoryNameForPDF = (category) => {
      // First try to get the translation directly
      const translationKey = `expenses.${category.toLowerCase().replace(/\s+/g, '_')}`;
      const translated = t(translationKey);

      // If translation exists and is different from the key, use it
      if (translated !== translationKey) {
        return translated;
      }

      // Fallback mapping for specific categories
      const categoryMap = {
        'Fuel Expenses': t('expenses.fuel_expenses'),
        'Driver Commissions': t('accounting.driver_commissions'),
        'Vehicle Maintenance': t('expenses.maintenance_expenses'),
        'Office Expenses': t('expenses.office_expenses'),
        'Utilities': t('accounting.utilities'),
        'Insurance': t('expenses.insurance_expenses'),
        'Other Expenses': t('expenses.other_expenses'),
        'operating_expense': t('accounting.operating_expenses'),
        'administrative_expense': t('expenses.office_expenses')
      };

      return categoryMap[category] || t('expenses.other_expenses');
    };

    content += `${t('accounting.expense_breakdown')}:\n`;
    if (Object.keys(expenseBreakdown).length > 0) {
      Object.entries(expenseBreakdown).forEach(([category, amount]) => {
        content += `${getCategoryNameForPDF(category)}: SAR ${amount.toLocaleString()}\n`;
      });
    } else {
      content += `${t('accounting.fuel_costs', 'Fuel Costs')}: SAR 0\n`;
      content += `${t('accounting.driver_commissions', 'Driver Commissions')}: SAR 0\n`;
      content += `${t('accounting.maintenance', 'Maintenance')}: SAR 0\n`;
      content += `${t('accounting.other_expenses', 'Other Expenses')}: SAR 0\n`;
    }

    return content;
  };

  const generateBalanceSheetContent = () => {
    let content = `${t('accounting.balance_sheet', 'BALANCE SHEET')}\n`;
    content += `${t('reports.as_of', 'As of')}: ${getDateRangeDisplay()}\n\n`;

    // Get real account balances from chart of accounts
    const assetAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'asset');
    const liabilityAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'liability');
    const equityAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'equity');

    // Group assets by category
    const currentAssets = assetAccounts.filter(acc => acc.category === 'current_asset');
    const fixedAssets = assetAccounts.filter(acc => acc.category === 'fixed_asset');

    // Calculate totals
    const totalCurrentAssets = currentAssets.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalFixedAssets = fixedAssets.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalAssets = totalCurrentAssets + totalFixedAssets;

    const totalLiabilities = liabilityAccounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalEquity = equityAccounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);

    content += `${t('accounting.assets', 'ASSETS')}:\n`;
    content += `${t('accounting.current_assets', 'Current Assets')}:\n`;
    currentAssets.forEach(account => {
      if (account.currentBalance !== 0) {
        content += `  ${account.accountNameAr || account.accountName}: SAR ${(account.currentBalance || 0).toLocaleString()}\n`;
      }
    });
    content += `${t('reports.total_current_assets', 'Total Current Assets')}: SAR ${totalCurrentAssets.toLocaleString()}\n\n`;

    content += `${t('reports.fixed_assets', 'Fixed Assets')}:\n`;
    fixedAssets.forEach(account => {
      if (account.currentBalance !== 0) {
        content += `  ${account.accountNameAr || account.accountName}: SAR ${(account.currentBalance || 0).toLocaleString()}\n`;
      }
    });
    content += `${t('reports.total_fixed_assets', 'Total Fixed Assets')}: SAR ${totalFixedAssets.toLocaleString()}\n\n`;

    content += `${t('accounting.total_assets', 'TOTAL ASSETS')}: SAR ${totalAssets.toLocaleString()}\n\n`;

    // Liabilities
    content += `${t('accounting.liabilities', 'LIABILITIES')}:\n`;
    liabilityAccounts.forEach(account => {
      if (account.currentBalance !== 0) {
        content += `  ${account.accountNameAr || account.accountName}: SAR ${(account.currentBalance || 0).toLocaleString()}\n`;
      }
    });
    content += `${t('accounting.total_liabilities', 'Total Liabilities')}: SAR ${totalLiabilities.toLocaleString()}\n\n`;

    // Equity
    content += `${t('accounting.equity', 'EQUITY')}:\n`;
    equityAccounts.forEach(account => {
      if (account.currentBalance !== 0) {
        content += `  ${account.accountNameAr || account.accountName}: SAR ${(account.currentBalance || 0).toLocaleString()}\n`;
      }
    });
    content += `${t('accounting.total_equity', 'Total Equity')}: SAR ${totalEquity.toLocaleString()}\n\n`;

    content += `${t('reports.total_liab_equity', 'TOTAL LIABILITIES & EQUITY')}: SAR ${(totalLiabilities + totalEquity).toLocaleString()}\n`;

    return content;
  };

  const generateCashFlowContent = () => {
    let content = `${t('accounting.cash_flow_statement', 'CASH FLOW STATEMENT')}\n\n`;

    // Calculate real cash flow from journal entries
    const investingTransactions = reportData.journalEntries.filter(entry =>
      entry.status === 'posted' &&
      entry.lineItems?.some(item =>
        item.accountCode?.startsWith('15') || // Fixed assets
        item.accountCode?.startsWith('16') || // Equipment
        item.accountCode?.startsWith('17')    // Accumulated depreciation
      )
    );

    const financingTransactions = reportData.journalEntries.filter(entry =>
      entry.status === 'posted' &&
      entry.lineItems?.some(item =>
        item.accountCode?.startsWith('25') || // Loans
        item.accountCode?.startsWith('26') || // Equipment financing
        item.accountCode?.startsWith('30')    // Capital
      )
    );

    // Calculate investing cash flow from actual transactions
    let investingCashFlow = 0;
    investingTransactions.forEach(entry => {
      entry.lineItems?.forEach(item => {
        if (item.accountCode?.startsWith('10')) { // Cash accounts
          investingCashFlow += (item.debit || 0) - (item.credit || 0);
        }
      });
    });

    // Calculate financing cash flow from actual transactions
    let financingCashFlow = 0;
    financingTransactions.forEach(entry => {
      entry.lineItems?.forEach(item => {
        if (item.accountCode?.startsWith('10')) { // Cash accounts
          financingCashFlow += (item.debit || 0) - (item.credit || 0);
        }
      });
    });

    const receivablesBalance = reportData.chartOfAccounts.find(acc => acc.accountCode === '1100')?.currentBalance || 0;
    const payablesBalance = reportData.chartOfAccounts.find(acc => acc.accountCode === '2000')?.currentBalance || 0;
    const operatingCashFlow = filteredFinancialData.profit - receivablesBalance + payablesBalance;

    content += `${t('reports.operating_activities')}:\n`;
    content += `${t('accounting.net_income')}: SAR ${filteredFinancialData.profit.toLocaleString()}\n`;
    content += `${t('reports.changes_in_working_capital')}: SAR ${(-receivablesBalance).toLocaleString()}\n`;
    content += `${t('reports.accounts_payable_change')}: SAR ${payablesBalance.toLocaleString()}\n`;
    content += `${t('reports.net_cash_from_operations')}: SAR ${operatingCashFlow.toLocaleString()}\n\n`;

    content += `${t('reports.investing_activities')}:\n`;
    if (investingTransactions.length > 0) {
      investingTransactions.forEach(entry => {
        content += `${entry.description}: SAR ${entry.lineItems?.filter(item => item.accountCode?.startsWith('10'))
          .reduce((sum, item) => sum + ((item.debit || 0) - (item.credit || 0)), 0)
          .toLocaleString()}\n`;
      });
    } else {
      content += `${t('reports.equipment_purchases')}: SAR 0\n`;
    }
    content += `${t('reports.net_cash_from_investing')}: SAR ${investingCashFlow.toLocaleString()}\n\n`;

    content += `${t('reports.financing_activities')}:\n`;
    if (financingTransactions.length > 0) {
      financingTransactions.forEach(entry => {
        content += `${entry.description}: SAR ${entry.lineItems?.filter(item => item.accountCode?.startsWith('10'))
          .reduce((sum, item) => sum + ((item.debit || 0) - (item.credit || 0)), 0)
          .toLocaleString()}\n`;
      });
    } else {
      content += `${t('reports.owner_contributions')}: SAR 0\n`;
    }
    content += `${t('reports.net_cash_from_financing')}: SAR ${financingCashFlow.toLocaleString()}\n\n`;

    const netCashFlow = operatingCashFlow + investingCashFlow + financingCashFlow;
    content += `${t('reports.net_change_in_cash')}: SAR ${netCashFlow.toLocaleString()}\n`;

    return content;
  };

  const generateTaxReportContent = () => {
    let content = `${t('accounting.vat_tax_report')}\n\n`;

    const vatOnSales = filteredFinancialData.filteredInvoices?.reduce((sum, inv) => sum + (inv.vatAmount || 0), 0) || 0;
    const vatOnPurchases = filteredFinancialData.filteredExpenses?.reduce((sum, exp) => sum + (exp.vatAmount || 0), 0) || 0;
    const netVAT = vatOnSales - vatOnPurchases;

    content += `${t('accounting.vat_on_sales')}: SAR ${vatOnSales.toLocaleString()}\n`;
    content += `${t('accounting.vat_on_purchases')}: SAR ${vatOnPurchases.toLocaleString()}\n`;
    content += `${t('accounting.net_vat_payable')}: SAR ${netVAT.toLocaleString()}\n\n`;

    content += `${t('accounting.sales_breakdown')}:\n`;
    filteredFinancialData.filteredInvoices?.forEach(invoice => {
      content += `${t('accounting.invoice')} ${invoice.invoiceNumber || invoice.id}: SAR ${(invoice.vatAmount || 0).toLocaleString()}\n`;
    });

    content += `\n${t('accounting.purchase_breakdown')}:\n`;
    filteredFinancialData.filteredExpenses?.forEach(expense => {
      if (expense.vatAmount > 0) {
        content += `${expense.description}: SAR ${expense.vatAmount.toLocaleString()}\n`;
      }
    });

    return content;
  };

  const generateAgingReportContent = () => {
    let content = `${t('accounting.accounts_receivable_aging_report')}\n\n`;

    const today = new Date();
    const aging = {
      current: 0,
      days30: 0,
      days60: 0,
      days90: 0,
      over90: 0
    };

    reportData.invoices.forEach(invoice => {
      if (invoice.paymentStatus !== 'paid') {
        const invoiceDate = new Date(invoice.issueDate || invoice.createdAt?.toDate?.() || invoice.createdAt);
        const daysDiff = Math.floor((today - invoiceDate) / (1000 * 60 * 60 * 24));
        const amount = invoice.remainingAmount || invoice.totalAmount || 0;

        if (daysDiff <= 30) aging.current += amount;
        else if (daysDiff <= 60) aging.days30 += amount;
        else if (daysDiff <= 90) aging.days60 += amount;
        else if (daysDiff <= 120) aging.days90 += amount;
        else aging.over90 += amount;
      }
    });

    content += `${t('accounting.current_30_days')}: SAR ${aging.current.toLocaleString()}\n`;
    content += `${t('accounting.days_31_60')}: SAR ${aging.days30.toLocaleString()}\n`;
    content += `${t('accounting.days_61_90')}: SAR ${aging.days60.toLocaleString()}\n`;
    content += `${t('accounting.days_91_120')}: SAR ${aging.days90.toLocaleString()}\n`;
    content += `${t('accounting.over_120_days')}: SAR ${aging.over90.toLocaleString()}\n\n`;

    const total = aging.current + aging.days30 + aging.days60 + aging.days90 + aging.over90;
    content += `${t('accounting.total_outstanding')}: SAR ${total.toLocaleString()}\n`;

    return content;
  };

  const generateTrialBalanceContent = () => {
    let content = `${t('accounting.trial_balance')}\n\n`;
    content += `${t('accounting.account_name')}                    ${t('accounting.debit')}        ${t('accounting.credit')}\n`;
    content += '-'.repeat(50) + '\n';

    // This would normally come from actual account balances
    // For now, we'll use the chart of accounts structure
    reportData.chartOfAccounts.forEach(account => {
      const balance = account.balance || 0;
      const debit = balance >= 0 ? balance : 0;
      const credit = balance < 0 ? Math.abs(balance) : 0;

      content += `${account.accountName.padEnd(30)} ${debit.toLocaleString().padStart(10)} ${credit.toLocaleString().padStart(10)}\n`;
    });

    return content;
  };

  const generateOverviewContent = () => {
    let content = `${t('accounting.financial_overview')}\n\n`;
    content += `${t('accounting.total_revenue')}: SAR ${filteredFinancialData.revenue.toLocaleString()}\n`;
    content += `${t('accounting.total_expenses')}: SAR ${filteredFinancialData.expenses.toLocaleString()}\n`;
    content += `${t('accounting.net_profit')}: SAR ${filteredFinancialData.profit.toLocaleString()}\n`;
    content += `${t('chart.profit_margin')}: ${filteredFinancialData.revenue > 0 ? ((filteredFinancialData.profit / filteredFinancialData.revenue) * 100).toFixed(1) : '0.0'}%\n\n`;

    const receivablesBalance = reportData.chartOfAccounts.find(acc => acc.accountCode === '1100')?.currentBalance || 0;
    const payablesBalance = reportData.chartOfAccounts.find(acc => acc.accountCode === '2000')?.currentBalance || 0;

    content += `${t('accounting.accounts_receivable')}: SAR ${receivablesBalance.toLocaleString()}\n`;
    content += `${t('accounting.accounts_payable')}: SAR ${payablesBalance.toLocaleString()}\n`;
    content += `${t('accounting.vat_payable')}: SAR ${filteredFinancialData.vatPayable.toLocaleString()}\n`;

    return content;
  };

  // Helper function to format date range display
  const getDateRangeDisplay = () => {
    const { startDate, endDate } = getDateRangeFilter();

    // Format date as DD/MM/YYYY
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    if (dateRange === 'current_month' || dateRange === 'last_month') {
      const monthNumber = startDate.getMonth() + 1;
      const year = startDate.getFullYear();
      return `${year} ${t('common.month', 'شهر')} ${monthNumber} (${formatDate(startDate)} - ${formatDate(endDate)})`;
    } else if (dateRange === 'current_year' || dateRange === 'last_year') {
      return `${startDate.getFullYear()} (${formatDate(startDate)} - ${formatDate(endDate)})`;
    } else if (dateRange === 'current_quarter' || dateRange === 'last_quarter') {
      const quarter = Math.floor(startDate.getMonth() / 3) + 1;
      return `Q${quarter} ${startDate.getFullYear()} (${formatDate(startDate)} - ${formatDate(endDate)})`;
    } else {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
  };

  const renderOverviewReport = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="card-body text-center">
            <div className="text-3xl font-bold">SAR {filteredFinancialData.revenue.toLocaleString()}</div>
            <div className="text-sm opacity-90">{t('accounting.total_revenue')}</div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-red-500 to-red-600 text-white">
          <div className="card-body text-center">
            <div className="text-3xl font-bold">SAR {filteredFinancialData.expenses.toLocaleString()}</div>
            <div className="text-sm opacity-90">{t('accounting.total_expenses')}</div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-green-500 to-green-600 text-white">
          <div className="card-body text-center">
            <div className="text-3xl font-bold">SAR {filteredFinancialData.profit.toLocaleString()}</div>
            <div className="text-sm opacity-90">{t('accounting.net_profit')}</div>
          </div>
        </div>

        <div className="card bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <div className="card-body text-center">
            <div className="text-3xl font-bold">{filteredFinancialData.revenue > 0 ? ((filteredFinancialData.profit / filteredFinancialData.revenue) * 100).toFixed(1) : '0.0'}%</div>
            <div className="text-sm opacity-90">{t('chart.profit_margin')}</div>
          </div>
        </div>
      </div>

      {/* Financial Health Indicators */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('reports.revenue_vs_expenses', 'Revenue vs Expenses')}</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span>{t('accounting.revenue')}</span>
                  <span className="font-bold">SAR {filteredFinancialData.revenue.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span>{t('accounting.expenses')}</span>
                  <span className="font-bold">SAR {filteredFinancialData.expenses.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-red-600 h-2 rounded-full"
                    style={{ width: `${filteredFinancialData.revenue > 0 ? (filteredFinancialData.expenses / filteredFinancialData.revenue) * 100 : 0}%` }}></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span>{t('reports.profit', 'Profit')}</span>
                  <span className="font-bold text-green-600">SAR {filteredFinancialData.profit.toLocaleString()}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${filteredFinancialData.revenue > 0 ? (filteredFinancialData.profit / filteredFinancialData.revenue) * 100 : 0}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('reports.outstanding_amounts', 'Outstanding Amounts')}</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                <div>
                  <div className="font-medium">{t('accounting.accounts_receivable')}</div>
                  <div className="text-sm text-gray-500">{t('accounting.money_owed_to_us')}</div>
                </div>
                <div className="text-lg font-bold text-yellow-600">
                  SAR {(reportData.chartOfAccounts.find(acc => acc.accountCode === '1100')?.currentBalance || 0).toLocaleString()}
                </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <div>
                  <div className="font-medium">{t('accounting.accounts_payable')}</div>
                  <div className="text-sm text-gray-500">{t('reports.money_we_owe', 'Money we owe')}</div>
                </div>
                <div className="text-lg font-bold text-orange-600">
                  SAR {(reportData.chartOfAccounts.find(acc => acc.accountCode === '2000')?.currentBalance || 0).toLocaleString()}
                </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <div>
                  <div className="font-medium">{t('accounting.vat_payable')}</div>
                  <div className="text-sm text-gray-500">{t('reports.vat_due_government', 'VAT due to government')}</div>
                </div>
                <div className="text-lg font-bold text-red-600">
                  SAR {filteredFinancialData.vatPayable.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProfitLossReport = () => {
    // Calculate real expense breakdown from actual expense data
    const expenseBreakdown = {};
    filteredFinancialData.filteredExpenses?.forEach(expense => {
      const category = expense.category || 'Other Expenses';
      if (!expenseBreakdown[category]) {
        expenseBreakdown[category] = 0;
      }
      expenseBreakdown[category] += expense.totalAmount || expense.amount || 0;
    });

    // Get revenue breakdown from chart of accounts
    const revenueAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'revenue');
    const revenueBreakdown = {};
    revenueAccounts.forEach(account => {
      if (account.currentBalance > 0) {
        // Use Arabic name if available, otherwise English name
        const accountName = account.accountNameAr || account.accountName;
        revenueBreakdown[accountName] = account.currentBalance;
      }
    });

    // Helper function to get translated category name
    const getCategoryName = (category) => {
      // Handle category IDs (like 'fuel', 'office', 'maintenance') used in the system
      const categoryIdMap = {
        'fuel': t('expenses.fuel_expenses'),
        'office': t('expenses.office_expenses'),
        'maintenance': t('expenses.maintenance_expenses'),
        'insurance': t('expenses.insurance_expenses'),
        'salary': t('expenses.salary_expenses'),
        'marketing': t('expenses.marketing_expenses'),
        'utility': t('expenses.utility_expenses'),
        'equipment': t('expenses.equipment_expenses'),
        'legal': t('expenses.legal_expenses'),
        'travel': t('expenses.travel_expenses'),
        'truck_washing': t('expenses.truck_washing_expenses'),
        'oil_change': t('expenses.oil_change_expenses'),
        'commission': t('expenses.commission_expenses'),
        'other': t('expenses.other_expenses')
      };

      // Check if it's a category ID first
      if (categoryIdMap[category]) {
        return categoryIdMap[category];
      }

      // Try direct translation key
      const translationKey = `expenses.${category.toLowerCase().replace(/\s+/g, '_')}`;
      const translated = t(translationKey);
      if (translated !== translationKey) {
        return translated;
      }

      // Fallback mapping for full English category names
      const categoryMap = {
        'Fuel Expenses': t('expenses.fuel_expenses'),
        'Driver Commissions': t('accounting.driver_commissions'),
        'Vehicle Maintenance': t('expenses.maintenance_expenses'),
        'Office Expenses': t('expenses.office_expenses'),
        'Utilities': t('accounting.utilities'),
        'Insurance': t('expenses.insurance_expenses'),
        'Other Expenses': t('expenses.other_expenses'),
        'operating_expense': t('accounting.operating_expenses'),
        'administrative_expense': t('expenses.office_expenses')
      };

      return categoryMap[category] || t('expenses.other_expenses');
    };

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">{t('accounting.profit_loss_statement')}</h3>
          <div className="text-sm text-gray-500">{t('invoices.period')}: {getDateRangeDisplay()}</div>
        </div>
        <div className="card-body">
          <table className="table w-full">
            <tbody>
              {/* Revenue Section */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg">{t('accounting.revenue')}</td>
                <td></td>
                <td></td>
              </tr>
              {Object.keys(revenueBreakdown).length > 0 ? (
                Object.entries(revenueBreakdown).map(([accountName, amount]) => (
                  <tr key={accountName}>
                    <td className="pl-4">{accountName}</td>
                    <td></td>
                    <td className="text-right font-medium">SAR {amount.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="pl-4">{t('accounting.transportation_revenue')}</td>
                  <td></td>
                  <td className="text-right font-medium">SAR {filteredFinancialData.revenue.toLocaleString()}</td>
                </tr>
              )}
              <tr className="border-b">
                <td className="font-semibold">{t('accounting.total_revenue')}</td>
                <td></td>
                <td className="text-right font-bold">SAR {filteredFinancialData.revenue.toLocaleString()}</td>
              </tr>

              {/* Expenses Section */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg pt-4">{t('accounting.expenses')}</td>
                <td></td>
                <td></td>
              </tr>
              {Object.keys(expenseBreakdown).length > 0 ? (
                Object.entries(expenseBreakdown).map(([category, amount]) => (
                  <tr key={category}>
                    <td className="pl-4">{getCategoryName(category)}</td>
                    <td></td>
                    <td className="text-right">SAR {amount.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <>
                  <tr>
                    <td className="pl-4">{t('accounting.fuel_costs')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                  <tr>
                    <td className="pl-4">{t('accounting.driver_commissions')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                  <tr>
                    <td className="pl-4">{t('accounting.maintenance')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                  <tr>
                    <td className="pl-4">{t('accounting.other_expenses')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                </>
              )}
              <tr className="border-b">
                <td className="font-semibold">{t('accounting.total_expenses')}</td>
                <td></td>
                <td className="text-right font-bold">SAR {filteredFinancialData.expenses.toLocaleString()}</td>
              </tr>

              {/* Net Income */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg text-green-600 pt-4">{t('accounting.net_income')}</td>
                <td></td>
                <td className="text-right font-bold text-green-600 text-lg">SAR {filteredFinancialData.profit.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCashFlowReport = () => {
    // Calculate real cash flow from journal entries
    const investingTransactions = reportData.journalEntries.filter(entry =>
      entry.status === 'posted' &&
      entry.lineItems?.some(item =>
        item.accountCode?.startsWith('15') || // Fixed assets
        item.accountCode?.startsWith('16') || // Equipment
        item.accountCode?.startsWith('17')    // Accumulated depreciation
      )
    );

    const financingTransactions = reportData.journalEntries.filter(entry =>
      entry.status === 'posted' &&
      entry.lineItems?.some(item =>
        item.accountCode?.startsWith('25') || // Loans
        item.accountCode?.startsWith('26') || // Equipment financing
        item.accountCode?.startsWith('30')    // Capital
      )
    );

    // Calculate investing cash flow from actual transactions
    let investingCashFlow = 0;
    investingTransactions.forEach(entry => {
      entry.lineItems?.forEach(item => {
        if (item.accountCode?.startsWith('10')) { // Cash accounts
          investingCashFlow += (item.debit || 0) - (item.credit || 0);
        }
      });
    });

    // Calculate financing cash flow from actual transactions
    let financingCashFlow = 0;
    financingTransactions.forEach(entry => {
      entry.lineItems?.forEach(item => {
        if (item.accountCode?.startsWith('10')) { // Cash accounts
          financingCashFlow += (item.debit || 0) - (item.credit || 0);
        }
      });
    });

    const operatingCashFlow = filteredFinancialData.profit - financialData.receivables + financialData.payables;
    const netCashFlow = operatingCashFlow + investingCashFlow + financingCashFlow;

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">{t('accounting.cash_flow_statement')}</h3>
          <div className="text-sm text-gray-500">{t('invoices.period')}: {getDateRangeDisplay()}</div>
        </div>
        <div className="card-body">
          <table className="table w-full">
            <tbody>
              {/* Operating Activities */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg">{t('reports.operating_activities', 'OPERATING ACTIVITIES')}</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td className="pl-4">{t('accounting.net_income')}</td>
                <td></td>
                <td className="text-right font-medium">SAR {filteredFinancialData.profit.toLocaleString()}</td>
              </tr>
              <tr>
                <td className="pl-4">{t('reports.changes_in_working_capital', 'Changes in Working Capital:')}</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td className="pl-8">{t('reports.accounts_receivable_change', 'Accounts Receivable')}</td>
                <td></td>
                <td className="text-right">SAR {(-financialData.receivables).toLocaleString()}</td>
              </tr>
              <tr>
                <td className="pl-8">{t('reports.accounts_payable_change', 'Accounts Payable')}</td>
                <td></td>
                <td className="text-right">SAR {financialData.payables.toLocaleString()}</td>
              </tr>
              <tr className="border-b">
                <td className="font-semibold">{t('reports.net_cash_from_operations', 'Net Cash from Operating Activities')}</td>
                <td></td>
                <td className="text-right font-bold">SAR {operatingCashFlow.toLocaleString()}</td>
              </tr>

              {/* Investing Activities */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg pt-4">{t('reports.investing_activities', 'INVESTING ACTIVITIES')}</td>
                <td></td>
                <td></td>
              </tr>
              {investingTransactions.length > 0 ? (
                investingTransactions.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td className="pl-4">{entry.description}</td>
                    <td></td>
                    <td className="text-right">
                      SAR {entry.lineItems?.filter(item => item.accountCode?.startsWith('10'))
                        .reduce((sum, item) => sum + ((item.debit || 0) - (item.credit || 0)), 0)
                        .toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <>
                  <tr>
                    <td className="pl-4">{t('reports.equipment_purchases', 'Equipment Purchases')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                  <tr>
                    <td className="pl-4">{t('reports.vehicle_purchases', 'Vehicle Purchases')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                </>
              )}
              <tr className="border-b">
                <td className="font-semibold">{t('reports.net_cash_from_investing', 'Net Cash from Investing Activities')}</td>
                <td></td>
                <td className="text-right font-bold">SAR {investingCashFlow.toLocaleString()}</td>
              </tr>

              {/* Financing Activities */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg pt-4">{t('reports.financing_activities', 'FINANCING ACTIVITIES')}</td>
                <td></td>
                <td></td>
              </tr>
              {financingTransactions.length > 0 ? (
                financingTransactions.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td className="pl-4">{entry.description}</td>
                    <td></td>
                    <td className="text-right">
                      SAR {entry.lineItems?.filter(item => item.accountCode?.startsWith('10'))
                        .reduce((sum, item) => sum + ((item.debit || 0) - (item.credit || 0)), 0)
                        .toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <>
                  <tr>
                    <td className="pl-4">{t('reports.owner_contributions', 'Owner Contributions')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                  <tr>
                    <td className="pl-4">{t('reports.loan_proceeds', 'Loan Proceeds')}</td>
                    <td></td>
                    <td className="text-right">SAR 0</td>
                  </tr>
                </>
              )}
              <tr className="border-b">
                <td className="font-semibold">{t('reports.net_cash_from_financing', 'Net Cash from Financing Activities')}</td>
                <td></td>
                <td className="text-right font-bold">SAR {financingCashFlow.toLocaleString()}</td>
              </tr>

              {/* Net Change */}
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg text-blue-600 pt-4">{t('reports.net_change_in_cash', 'NET CHANGE IN CASH')}</td>
                <td></td>
                <td className="text-right font-bold text-blue-600 text-lg">SAR {netCashFlow.toLocaleString()}</td>
              </tr>
              <tr>
                <td className="font-medium">{t('reports.cash_beginning', 'Cash at Beginning of Period')}</td>
                <td></td>
                <td className="text-right font-medium">
                  SAR {(reportData.chartOfAccounts.find(acc => acc.accountCode?.startsWith('100'))?.openingBalance || 0).toLocaleString()}
                </td>
              </tr>
              <tr className="border-b-2 border-gray-300">
                <td className="font-bold text-lg text-green-600">{t('reports.cash_end', 'CASH AT END OF PERIOD')}</td>
                <td></td>
                <td className="text-right font-bold text-green-600 text-lg">
                  SAR {(reportData.chartOfAccounts.find(acc => acc.accountCode?.startsWith('100'))?.currentBalance || 0).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTaxReport = () => {
    const vatOnSales = filteredFinancialData.filteredInvoices?.reduce((sum, inv) => sum + (inv.vatAmount || 0), 0) || 0;
    const vatOnPurchases = filteredFinancialData.filteredExpenses?.reduce((sum, exp) => sum + (exp.vatAmount || 0), 0) || 0;
    const netVAT = vatOnSales - vatOnPurchases;

    return (
      <div className="space-y-6">
        {/* VAT Summary */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('accounting.vat_summary', 'VAT Summary')}</h3>
            <div className="text-sm text-gray-500">{t('invoices.period')}: {getDateRangeDisplay()}</div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">SAR {vatOnSales.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.vat_on_sales', 'VAT on Sales')}</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">SAR {vatOnPurchases.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.vat_on_purchases', 'VAT on Purchases')}</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">SAR {netVAT.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.net_vat_payable', 'Net VAT Payable')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sales VAT Details */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('reports.sales_vat_details', 'Sales VAT Details')}</h3>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>{t('accounting.invoice_number')}</th>
                    <th>{t('accounting.client')}</th>
                    <th>{t('accounting.date')}</th>
                    <th>{t('accounting.subtotal')}</th>
                    <th>{t('accounting.vat_amount')}</th>
                    <th>{t('accounting.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFinancialData.filteredInvoices?.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.invoiceNumber || invoice.id}</td>
                      <td>{invoice.clientName || invoice.client?.name || 'Unknown'}</td>
                      <td>{new Date(invoice.issueDate || invoice.createdAt?.toDate?.() || invoice.createdAt).toLocaleDateString()}</td>
                      <td>SAR {(invoice.subtotal || 0).toLocaleString()}</td>
                      <td>SAR {(invoice.vatAmount || 0).toLocaleString()}</td>
                      <td>SAR {(invoice.finalTotal || invoice.total || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td colSpan="4">{t('accounting.total')}</td>
                    <td>SAR {vatOnSales.toLocaleString()}</td>
                    <td>SAR {filteredFinancialData.revenue.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Purchase VAT Details */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('reports.purchase_vat_details', 'Purchase VAT Details')}</h3>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>{t('accounting.description')}</th>
                    <th>{t('accounting.category')}</th>
                    <th>{t('accounting.date')}</th>
                    <th>{t('accounting.amount')}</th>
                    <th>{t('accounting.vat_amount')}</th>
                    <th>{t('accounting.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFinancialData.filteredExpenses?.filter(exp => exp.vatAmount > 0).map((expense) => (
                    <tr key={expense.id}>
                      <td>{expense.description}</td>
                      <td>{expense.category}</td>
                      <td>{new Date(expense.date || expense.createdAt?.toDate?.() || expense.createdAt).toLocaleDateString()}</td>
                      <td>SAR {(expense.amount || 0).toLocaleString()}</td>
                      <td>SAR {(expense.vatAmount || 0).toLocaleString()}</td>
                      <td>SAR {(expense.totalAmount || expense.amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td colSpan="4">{t('accounting.total')}</td>
                    <td>SAR {vatOnPurchases.toLocaleString()}</td>
                    <td>SAR {filteredFinancialData.filteredExpenses?.reduce((sum, exp) => sum + (exp.totalAmount || exp.amount || 0), 0).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBalanceSheetReport = () => {
    // Get real account balances from chart of accounts
    const assetAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'asset');
    const liabilityAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'liability');
    const equityAccounts = reportData.chartOfAccounts.filter(acc => acc.accountType === 'equity');

    // Group assets by category
    const currentAssets = assetAccounts.filter(acc => acc.category === 'current_asset');
    const fixedAssets = assetAccounts.filter(acc => acc.category === 'fixed_asset');

    // Group liabilities by category
    const currentLiabilities = liabilityAccounts.filter(acc => acc.category === 'current_liability');
    const longTermLiabilities = liabilityAccounts.filter(acc => acc.category === 'long_term_liability');

    // Calculate totals
    const totalCurrentAssets = currentAssets.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalFixedAssets = fixedAssets.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalAssets = totalCurrentAssets + totalFixedAssets;

    const totalCurrentLiabilities = currentLiabilities.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalLongTermLiabilities = longTermLiabilities.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;

    const totalEquity = equityAccounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">{t('accounting.balance_sheet')}</h3>
          <div className="text-sm text-gray-500">{t('reports.as_of', 'As of')}: {getDateRangeDisplay()}</div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Assets */}
            <div>
              <table className="table w-full">
                <tbody>
                  <tr className="border-b-2 border-gray-300">
                    <td className="font-bold text-lg">{t('accounting.assets')}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="font-semibold">{t('accounting.current_assets')}</td>
                    <td></td>
                  </tr>
                  {currentAssets.length > 0 ? (
                    currentAssets.map(account => (
                      <tr key={account.id}>
                        <td className="pl-4">{account.accountNameAr || account.accountName}</td>
                        <td className="text-right">SAR {(account.currentBalance || 0).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td className="pl-4">{t('accounting.cash')}</td>
                        <td className="text-right">SAR 0</td>
                      </tr>
                      <tr>
                        <td className="pl-4">{t('accounting.accounts_receivable')}</td>
                        <td className="text-right">SAR {financialData.receivables.toLocaleString()}</td>
                      </tr>
                    </>
                  )}
                  <tr className="border-b">
                    <td className="font-semibold">{t('reports.total_current_assets')}</td>
                    <td className="text-right font-bold">SAR {(totalCurrentAssets || financialData.receivables).toLocaleString()}</td>
                  </tr>

                  <tr>
                    <td className="font-semibold pt-4">{t('reports.fixed_assets')}</td>
                    <td></td>
                  </tr>
                  {fixedAssets.length > 0 ? (
                    fixedAssets.map(account => (
                      <tr key={account.id}>
                        <td className="pl-4">{account.accountNameAr || account.accountName}</td>
                        <td className="text-right">SAR {(account.currentBalance || 0).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td className="pl-4">{t('chart.vehicles')}</td>
                        <td className="text-right">SAR 0</td>
                      </tr>
                      <tr>
                        <td className="pl-4">{t('accounting.equipment')}</td>
                        <td className="text-right">SAR 0</td>
                      </tr>
                    </>
                  )}
                  <tr className="border-b">
                    <td className="font-semibold">{t('reports.total_fixed_assets')}</td>
                    <td className="text-right font-bold">SAR {totalFixedAssets.toLocaleString()}</td>
                  </tr>

                  <tr className="border-b-2 border-gray-300">
                    <td className="font-bold text-lg">{t('accounting.total_assets')}</td>
                    <td className="text-right font-bold text-lg">SAR {(totalAssets || financialData.receivables).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Liabilities & Equity */}
            <div>
              <table className="table w-full">
                <tbody>
                  <tr className="border-b-2 border-gray-300">
                    <td className="font-bold text-lg">{t('reports.liabilities_equity')}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="font-semibold">{t('accounting.current_liabilities')}</td>
                    <td></td>
                  </tr>
                  {currentLiabilities.length > 0 ? (
                    currentLiabilities.map(account => (
                      <tr key={account.id}>
                        <td className="pl-4">{account.accountNameAr || account.accountName}</td>
                        <td className="text-right">SAR {(account.currentBalance || 0).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td className="pl-4">{t('accounting.accounts_payable')}</td>
                        <td className="text-right">SAR {financialData.payables.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td className="pl-4">{t('accounting.vat_payable')}</td>
                        <td className="text-right">SAR {financialData.vatPayable.toLocaleString()}</td>
                      </tr>
                    </>
                  )}
                  {longTermLiabilities.length > 0 && (
                    <>
                      <tr>
                        <td className="font-semibold pt-2">{t('accounting.long_term_liabilities')}</td>
                        <td></td>
                      </tr>
                      {longTermLiabilities.map(account => (
                        <tr key={account.id}>
                          <td className="pl-4">{account.accountNameAr || account.accountName}</td>
                          <td className="text-right">SAR {(account.currentBalance || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  <tr className="border-b">
                    <td className="font-semibold">{t('accounting.total_liabilities')}</td>
                    <td className="text-right font-bold">SAR {(totalLiabilities || (financialData.payables + financialData.vatPayable)).toLocaleString()}</td>
                  </tr>

                  <tr>
                    <td className="font-semibold pt-4">{t('accounting.equity')}</td>
                    <td></td>
                  </tr>
                  {equityAccounts.length > 0 ? (
                    equityAccounts.map(account => (
                      <tr key={account.id}>
                        <td className="pl-4">{account.accountNameAr || account.accountName}</td>
                        <td className="text-right">SAR {(account.currentBalance || 0).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td className="pl-4">{t('chart.capital')}</td>
                        <td className="text-right">SAR 0</td>
                      </tr>
                      <tr>
                        <td className="pl-4">{t('accounting.retained_earnings')}</td>
                        <td className="text-right">SAR {filteredFinancialData.profit.toLocaleString()}</td>
                      </tr>
                    </>
                  )}
                  <tr className="border-b">
                    <td className="font-semibold">{t('accounting.total_equity')}</td>
                    <td className="text-right font-bold">SAR {(totalEquity || filteredFinancialData.profit).toLocaleString()}</td>
                  </tr>

                  <tr className="border-b-2 border-gray-300">
                    <td className="font-bold text-lg">{t('reports.total_liab_equity')}</td>
                    <td className="text-right font-bold text-lg">SAR {((totalLiabilities + totalEquity) || (financialData.payables + financialData.vatPayable + filteredFinancialData.profit)).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTrialBalanceReport = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-semibold">{t('accounting.trial_balance')}</h3>
        <div className="text-sm text-gray-500">As of {new Date().toLocaleDateString()}</div>
      </div>
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>{t('accounting.account_code')}</th>
                <th>{t('accounting.account_name', 'Account Name')}</th>
                <th className="text-right">{t('accounting.debit', 'Debit')}</th>
                <th className="text-right">{t('accounting.credit', 'Credit')}</th>
              </tr>
            </thead>
            <tbody>
              {reportData.chartOfAccounts.map((account, index) => {
                const currentBalance = account.currentBalance || 0;
                let debitBalance = 0;
                let creditBalance = 0;

                // Determine debit/credit based on account type and balance
                if (['asset', 'expense'].includes(account.accountType)) {
                  // Debit normal balance accounts
                  if (currentBalance >= 0) {
                    debitBalance = currentBalance;
                  } else {
                    creditBalance = Math.abs(currentBalance);
                  }
                } else {
                  // Credit normal balance accounts (liability, equity, revenue)
                  if (currentBalance >= 0) {
                    creditBalance = currentBalance;
                  } else {
                    debitBalance = Math.abs(currentBalance);
                  }
                }

                return (
                  <tr key={account.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td>{account.accountCode}</td>
                    <td>{account.accountNameAr || account.accountName}</td>
                    <td className="text-right">{debitBalance > 0 ? `SAR ${debitBalance.toLocaleString()}` : '-'}</td>
                    <td className="text-right">{creditBalance > 0 ? `SAR ${creditBalance.toLocaleString()}` : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-bold border-t-2">
                <td colSpan="2">{t('accounting.total', t('common.total'))}</td>
                <td className="text-right">
                  SAR {reportData.chartOfAccounts.reduce((sum, acc) => {
                    const currentBalance = acc.currentBalance || 0;
                    if (['asset', 'expense'].includes(acc.accountType)) {
                      return sum + Math.max(currentBalance, 0);
                    } else {
                      return sum + Math.max(-currentBalance, 0);
                    }
                  }, 0).toLocaleString()}
                </td>
                <td className="text-right">
                  SAR {reportData.chartOfAccounts.reduce((sum, acc) => {
                    const currentBalance = acc.currentBalance || 0;
                    if (['asset', 'expense'].includes(acc.accountType)) {
                      return sum + Math.max(-currentBalance, 0);
                    } else {
                      return sum + Math.max(currentBalance, 0);
                    }
                  }, 0).toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCurrentReport = () => {
    switch (selectedReport) {
      case 'overview':
        return renderOverviewReport();
      case 'profit_loss':
        return renderProfitLossReport();
      case 'balance_sheet':
        return renderBalanceSheetReport();
      case 'cash_flow':
        return renderCashFlowReport();
      case 'tax_report':
        return renderTaxReport();
      case 'aging_report':
        return renderAgingReport();
      case 'trial_balance':
        return renderTrialBalanceReport();
      case 'general_ledger':
        return renderGeneralLedgerReport();
      default:
        return renderOverviewReport();
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-800">{t('reports.processing', 'Processing Report')}</h3>
            <p className="text-gray-600 text-sm">{loadingMessage}</p>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '70%'}}></div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">{t('reports.please_wait', 'Please wait, this may take a few moments...')}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">{t('accounting.financial_reports')}</h2>
        <p className="text-sm text-gray-500">{t('accounting.comprehensive_analysis', 'Comprehensive financial analysis and reporting')}</p>
      </div>

      {/* Top Controls - Export Buttons Left, Date Range Center */}
      <div className="flex justify-between items-center">
        {/* Export Buttons - Left */}
        <div className="flex space-x-2">
          <button
            onClick={() => generateReport(selectedReport, 'pdf')}
            className="btn btn-primary"
            disabled={loading || !currentCompany?.id}
            title={!currentCompany?.id ? 'Company information required for reports' : ''}
          >
            {loading ? (
              <span className="loading loading-spinner loading-sm mr-2"></span>
            ) : (
              '📄'
            )}
            {t('accounting.generate_pdf', 'Generate PDF')}
          </button>
          
          <button
            onClick={() => generateReport(selectedReport, 'excel')}
            className="btn btn-success"
            disabled={loading || !currentCompany?.id}
            title={!currentCompany?.id ? 'Company information required for reports' : ''}
          >
            {loading ? (
              <span className="loading loading-spinner loading-sm mr-2"></span>
            ) : (
              '📊'
            )}
            {t('accounting.export_excel', 'Export Excel')}
          </button>
        </div>

        {/* Date Range Selector - Center */}
        <div className="flex flex-col items-center space-y-2">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">{t('reports.date_range', 'Date Range')}:</label>
            <select
              className="select select-bordered"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              {dateRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {dateRange === 'custom' && (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                className="input input-bordered input-sm"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                placeholder="Start Date"
              />
              <span>to</span>
              <input
                type="date"
                className="input input-bordered input-sm"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                placeholder="End Date"
              />
            </div>
          )}
        </div>

        {/* Empty space for balance */}
        <div className="w-32"></div>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {availableReports.map((report) => (
          <button
            key={report.id}
            onClick={() => setSelectedReport(report.id)}
            className={`p-4 rounded-lg border-2 text-left transition-all ${selectedReport === report.id
              ? `border-${report.color}-500 bg-${report.color}-50`
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="text-2xl mb-2">{report.icon}</div>
            <div className={`font-medium text-sm ${selectedReport === report.id ? `text-${report.color}-700` : 'text-gray-900'
              }`}>
              {report.name}
            </div>
            <div className="text-xs text-gray-500 mt-1">{report.description}</div>
          </button>
        ))}
      </div>

      {/* Report Content */}
      {renderCurrentReport()}
    </div>
  );

  // Additional report functions
  function renderAgingReport() {
    const today = new Date();
    const aging = {
      current: [],
      days30: [],
      days60: [],
      days90: [],
      over90: []
    };

    // Calculate aging for unpaid invoices
    reportData.invoices.forEach(invoice => {
      if (invoice.paymentStatus !== 'paid' && invoice.remainingAmount > 0) {
        const invoiceDate = new Date(invoice.issueDate || invoice.createdAt?.toDate?.() || invoice.createdAt);
        const daysDiff = Math.floor((today - invoiceDate) / (1000 * 60 * 60 * 24));

        if (daysDiff <= 30) aging.current.push(invoice);
        else if (daysDiff <= 60) aging.days30.push(invoice);
        else if (daysDiff <= 90) aging.days60.push(invoice);
        else if (daysDiff <= 120) aging.days90.push(invoice);
        else aging.over90.push(invoice);
      }
    });

    const totals = {
      current: aging.current.reduce((sum, inv) => sum + (inv.remainingAmount || inv.totalAmount || 0), 0),
      days30: aging.days30.reduce((sum, inv) => sum + (inv.remainingAmount || inv.totalAmount || 0), 0),
      days60: aging.days60.reduce((sum, inv) => sum + (inv.remainingAmount || inv.totalAmount || 0), 0),
      days90: aging.days90.reduce((sum, inv) => sum + (inv.remainingAmount || inv.totalAmount || 0), 0),
      over90: aging.over90.reduce((sum, inv) => sum + (inv.remainingAmount || inv.totalAmount || 0), 0)
    };

    const grandTotal = Object.values(totals).reduce((sum, amount) => sum + amount, 0);

    return (
      <div className="space-y-6">
        {/* Aging Summary */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('accounting.aging_summary', 'Accounts Receivable Aging Summary')}</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-xl font-bold text-green-600">SAR {totals.current.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.current_0_30', 'Current (0-30 days)')}</div>
                <div className="text-xs text-gray-500">{aging.current.length} invoices</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-xl font-bold text-yellow-600">SAR {totals.days30.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.days_31_60', '31-60 days')}</div>
                <div className="text-xs text-gray-500">{aging.days30.length} invoices</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-600">SAR {totals.days60.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.days_61_90', '61-90 days')}</div>
                <div className="text-xs text-gray-500">{aging.days60.length} invoices</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-xl font-bold text-red-600">SAR {totals.days90.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.days_91_120', '91-120 days')}</div>
                <div className="text-xs text-gray-500">{aging.days90.length} invoices</div>
              </div>
              <div className="text-center p-4 bg-red-100 rounded-lg">
                <div className="text-xl font-bold text-red-700">SAR {totals.over90.toLocaleString()}</div>
                <div className="text-sm text-gray-600">{t('reports.over_120', 'Over 120 days')}</div>
                <div className="text-xs text-gray-500">{aging.over90.length} invoices</div>
              </div>
            </div>
            <div className="mt-4 text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-800">SAR {grandTotal.toLocaleString()}</div>
              <div className="text-sm text-gray-600">{t('reports.total_outstanding', 'Total Outstanding')}</div>
            </div>
          </div>
        </div>

        {/* Detailed Aging Table */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">{t('reports.detailed_aging', 'Detailed Aging Report')}</h3>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>{t('accounting.invoice_number')}</th>
                    <th>{t('accounting.client')}</th>
                    <th>{t('accounting.invoice_date')}</th>
                    <th>{t('reports.days_outstanding', 'Days Outstanding')}</th>
                    <th>{t('reports.amount_due', 'Amount Due')}</th>
                    <th>{t('reports.aging_bucket', 'Aging Bucket')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...aging.current, ...aging.days30, ...aging.days60, ...aging.days90, ...aging.over90].map((invoice) => {
                    const invoiceDate = new Date(invoice.issueDate || invoice.createdAt?.toDate?.() || invoice.createdAt);
                    const daysDiff = Math.floor((today - invoiceDate) / (1000 * 60 * 60 * 24));
                    let bucket = '';
                    let bucketColor = '';

                    if (daysDiff <= 30) { bucket = 'Current'; bucketColor = 'text-green-600'; }
                    else if (daysDiff <= 60) { bucket = '31-60 days'; bucketColor = 'text-yellow-600'; }
                    else if (daysDiff <= 90) { bucket = '61-90 days'; bucketColor = 'text-orange-600'; }
                    else if (daysDiff <= 120) { bucket = '91-120 days'; bucketColor = 'text-red-600'; }
                    else { bucket = 'Over 120 days'; bucketColor = 'text-red-700'; }

                    return (
                      <tr key={invoice.id}>
                        <td>{invoice.invoiceNumber || invoice.id}</td>
                        <td>{invoice.clientName || invoice.client?.name || 'Unknown'}</td>
                        <td>{invoiceDate.toLocaleDateString()}</td>
                        <td>{daysDiff}</td>
                        <td>SAR {(invoice.remainingAmount || invoice.totalAmount || 0).toLocaleString()}</td>
                        <td className={bucketColor}>{bucket}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }



  function renderGeneralLedgerReport() {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">{t('accounting.general_ledger')}</h3>
          <div className="text-sm text-gray-500">{t('reports.all_account_transactions', 'All account transactions')}</div>
        </div>
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>{t('accounting.date')}</th>
                  <th>{t('accounting.entry_number')}</th>
                  <th>{t('accounting.account')}</th>
                  <th>{t('accounting.description')}</th>
                  <th className="text-right">{t('accounting.debit')}</th>
                  <th className="text-right">{t('accounting.credit')}</th>
                  <th className="text-right">{t('accounting.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {reportData.journalEntries
                  .filter(entry => entry.status === 'posted')
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .flatMap((entry, entryIndex) =>
                    entry.lineItems.map((lineItem, lineIndex) => ({
                      ...lineItem,
                      date: entry.date,
                      entryNumber: entry.entryNumber,
                      entryDescription: entry.description,
                      entryIndex
                    }))
                  )
                  .map((transaction, index) => (
                    <tr
                      key={index}
                      className={transaction.entryIndex % 2 === 0 ? 'bg-gray-200' : 'bg-white'}
                    >
                      <td>{new Date(transaction.date).toLocaleDateString()}</td>
                      <td>{transaction.entryNumber}</td>
                      <td>{transaction.accountCode} - {transaction.accountNameAr || transaction.accountName}</td>
                      <td>{transaction.description}</td>
                      <td className="text-right">{transaction.debit > 0 ? `SAR ${transaction.debit.toLocaleString()}` : '-'}</td>
                      <td className="text-right">{transaction.credit > 0 ? `SAR ${transaction.credit.toLocaleString()}` : '-'}</td>
                      <td className="text-right">-</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
};

export default ReportsTab;
